/* Shared New Citizen wiki content — rendered on the public website (/wiki)
   AND in the in-game main menu (MenuWikiPanel). Keep bodies in the same
   markdown-lite format: ## headings, "- " bullet blocks, "1." numbered blocks. */

export const WIKI_SECTIONS = [
  {
    id: "getting-started", label: "Getting Started", icon: "gamepad",
    body: `## Welcome to New Citizen
New Citizen is a hardcore roleplay sandbox set in a living Australian city. You create a citizen, pick a life — police, EMS, fire, civilian jobs, or the other side of the law — and the city reacts to what you do.

## Your first hour
1. **Create your citizen** — name, look, and backstory define how NPCs and players treat you
2. **Get legal income first** — courier runs and depot shifts teach you the city's layout while paying rent
3. **Buy a phone** — your lifeline for jobs, contacts and emergencies (in-game item)
4. **Register a vehicle** — unregistered vehicles attract police attention; the Lynx sedan is the classic starter
5. **Join a community** — solo RP is possible, but the city is built for crews (see the Community Board)

## The golden rule
Stay in character. The city runs on immersion — "breaking character" in public channels is the fastest way to get bounced from a serious server.`,
  },
  {
    id: "joining", label: "Joining & Spawning", icon: "map",
    body: `## The join sequence
Connecting to a server opens a cinematic entry that plays you into the city — including a "while you were away" recap: who's mayor, how many citizens are online, open emergencies, and the latest city wire posts.

## First arrival: create your citizen
New citizens register once per server — full name, date of birth, origin, outfit style and accent colour. This identity is what police, courts and other players see. Returning players skip straight to spawn selection.

## Pick your spawn point
- **Bornwood Central Station** — heart of the city, steps from shops and the courthouse
- **Bornwood General Hospital** — quiet northside start near the EMS depot
- **Route 68 Motel** — cheap beds on the strip, the classic fresh start
- **Bornwood Harbor** — dockside air, container yards and night work

## Choosing a faction
Citizen is always open. **Bornwood Sheriff's Department** and **Bornwood Fire Rescue** are open by default — but if the server owner enables *Whitelisted Factions* in the Owner Deck ruleset, you'll need staff approval before you can deploy with a department.`,
  },
  {
    id: "controls", label: "Controls & Interface", icon: "gamepad",
    body: `## Core controls (defaults, rebindable)
- **WASD** — move · **Shift** — sprint · **E** — interact · **F** — enter/exit vehicle
- **Tab** — inventory & citizen ID · **M** — city map with district overlays
- **T** — text radio (in-character) · **N** — push-to-talk voice
- **F1** — radial emote menu · **F2** — job panel (duty on/off)
- **F8** — mod console (Mod Hub exports appear here)

## The HUD
Minimal by design: vitals bottom-left, radio chatter top-left, minimap only in vehicles. Serious servers often run "hardcore HUD" with no minimap at all.

## Emergency controls (police/EMS/fire)
- **Q** — cycle emergency lighting stage (park, warning, full response)
- **R** — siren tone cycle (wail / yelp / phaser / custom samples)
- **G** — spotlight / alley light control on equipped vehicles`,
  },
  {
    id: "factions", label: "Jobs & Factions", icon: "users",
    body: `## The career ladder
Every job is player-run above the entry level. Entry jobs are NPC-managed; promotions come from other players.

## Emergency services
- **Bornwood Sheriff's Department** — cadet → deputy → detective → command. Full CAD/MDT dispatch tablet, court access, custom fleets from the Mod Hub
- **Bornwood Fire Rescue** — structure fires, vehicle extraction, EMS support rotations
- **EMS** — patient triage, ambulance runs, hospital rotations

## Faction whitelist
Departments are open by default. Server owners can require staff approval for Sheriff/Fire Rescue by enabling *Whitelisted Factions* (Owner Deck → Ruleset) — the spawn select will show a lock on gated factions.

## Civilian economy
Courier, tow truck, taxi, fishing, depot work, mechanic, real estate. Player businesses buy commercial lots and hire.

## The other side
The city's underworld is entirely player-driven — crews, turf, laundering fronts. High risk, high reward, and the police are players too.`,
  },
  {
    id: "casino", label: "The Gilded Spade", icon: "spade",
    body: `## The floor
The city's casino runs three tables, all settling against your real bank balance:
- **Roulette** — European single-zero wheel. Straights pay 35:1, dozens and columns 2:1, red/black and the even-money spots 1:1
- **Liberty Belle slots** — three reels, weighted symbols; three sevens pay 50x, any first-two pair pays 2x
- **Blackjack** — dealer stands on all 17s, blackjack pays 3:2, double down on any two cards
- **Multiplayer tables** — open a two-seat table and a rival can sit opposite you: both hands are face-up so you can watch them sweat, the dealer is shared, and the table settles once you've both finished

## The high-roller room
The back room runs the same games at $500–$25,000 a bet for players with a bankroll worth losing.

## The vault
The Gilded Spade's vault is a heist target — the biggest take in the city ($5,000–$12,000), but its silent alarm trips the instant you land the second notch. Units are already rolling before the door swings.`,
  },
  {
    id: "crypto", label: "Crypto Mining (CIV)", icon: "cpu",
    body: `## Your rig
Walk up to your mining rig and open the RigLink terminal. Start mining to hash **CivCoin (CIV)** — it accrues in a wallet buffer until you sell it to your bank.

## Heat management
Rigs heat up while hashing: past 70°C the rig thermal-throttles to 60% output, past 78°C to 35%, and at 82°C it shuts itself down. Let it cool between runs or invest in cooling.

## Rig upgrades (paid from your bank)
- **GPU array** — +18 MH/s per tier, three tiers; more hash means more CIV per hour
- **Cooling loop** — slower heat build and later throttle, three tiers

## The market
CIV's price drifts constantly between $8 and $60. Watch the sparkline — selling at the peak is the whole game.

## Rig sabotage
A rig left hashing while its owner is off doing something else is fair game: rivals can short-circuit an unattended rig and skim 40% of whatever is sitting in the wallet buffer. Seven times out of ten they get away clean — the rest, the breaker trips, the owner gets an alert naming the crew, and the saboteur eats a $500 fine. Collect often.`,
  },
  {
    id: "heists", label: "Heists & Robberies", icon: "vault",
    body: `## The jobs
Bank vaults, casino vaults, store registers and safes across the city are crackable. Every target is alarmed — police get a **211 silent alarm** on their CAD whether you crack it or trip it, so the chase is part of the job.

## Safecracking
A real three-notch dial: turn LEFT to the first notch, RIGHT to the second, LEFT again for the last. The dial wobbles when the fence is close. High-security vaults (the casino) trip their alarm part-way through — plan your exit before you start.

## Keypad hacks
A four-digit PIN with five attempts. After each guess: **green LEDs** mean right digit in the right place, **amber** means the digit exists but sits elsewhere. Miss the fifth and the pad fries itself.

## Bring a crew
A second player can join your active job as crowd control — holding the room while you work the dial. Crew jobs pay **+25%**, but the intimidation meter drains constantly; if it empties, the crowd panics and the job blows.`,
  },
  {
    id: "politics", label: "Politics & the Mayor", icon: "landmark",
    body: `## Running for office
Register your candidacy at City Hall (web) or the phone's City Hall app. Elections open with a registration window, then the whole server votes. The winner takes the office — including tax levers and the city wire.

## The death rule
A mayor who dies in office loses it: the city wire announces the vacancy and a special election opens automatically, so the seat is never empty for long. Server owners can flip **Persistent Mayor** in the Owner Deck ruleset to keep the role through death until removed.

## Taxes
The sitting mayor steers sales and income tax within city limits. Courts can review overreach — the government page is public record.`,
  },
  {
    id: "staff", label: "Staff & Admin Console", icon: "shield",
    body: `## The side-dock console
Server staff open a transparent side-dock admin console in-game — the world stays visible while you moderate. Live roster, world controls (weather, time of day, announcements), economy tuning, whitelist applications, ban list and a full command log.

## Player cards
Click any connected player for their detail card — Steam ID, IP address, ping, network flags — with every moderation action (warn, kick, ban, bring, goto, freeze, heal) one tap away.

## The Specpixel network watch
Staff can file evidence-backed network reports on problem players. Specpixel studio staff review every report on the web admin; an approved report flags that player on EVERY New Citizen server's admin console, network-wide.`,
  },
  {
    id: "economy", label: "Economy & Property", icon: "dollar",
    body: `## Money flows
Cash is physical — bank it or lose it when things go wrong. ATMs and bank branches are on the map; direct deposit exists for salaried jobs. Your phone's banking app handles transfers, and every web-side payment lands in the game through the bridge.

## Property
Apartments rent weekly. Houses and commercial lots sell through player real-estate agents. Storage in properties is raidable with a warrant — police RP consequence, not a bug.

## Vehicles
Buy at dealerships, register, insure. Custom vehicles built in the Vehicle Studio (lightbars, push bars, liveries, sirens, interior props) can be applied to your rides on servers that allow it.`,
  },
  {
    id: "servers", label: "Servers & Hosting", icon: "server",
    body: `## Playing online
Browse official and community servers on the **Servers & Community** page. Official servers run studio rules; community servers set their own — read them before you join.

## Hosting
Two paths: one-click rental via our hosting partner 4NetPlayers, or self-hosting the free dedicated server through SteamCMD. The full setup walkthrough, ports, config and mod installation live in **Host Your Own** on the servers page — and the Bridge Setup Wizard in the Owner Deck connects your server's database in minutes.

## Moderation
Fake "official" servers, cheat distribution and hate content get delisted network-wide. Report problem servers from their server card — every report reaches the moderation team.`,
  },
  {
    id: "modding", label: "Modding (Mod Hub)", icon: "wrench",
    body: `## The Modding Suite
New Citizen ships with a browser-based modding suite (currently dev-team access, public release on the roadmap):

- **Lightbar Studio** — design ELS flash patterns frame-by-frame on real lightbar models, export UE5 DataTable CSV
- **Vehicle Studio** — full pursuit-spec configurator: lighting, push bars, sirens, tint, liveries, interior props
- **Livery Painter** — stamp decals on the 3D body, bake 2048px UV sheets with a skin manifest
- **Mission Blueprint** — visual node-graph mission scripting, compiles to JSON + Lua
- **3D Sandbox & CAD Dispatch** — playtest missions in-browser before deploying

## The Mod Workshop
Servers can also install JSON mod packs — menu themes, boot sequences, penal codes, department structures and government systems — straight from the in-menu Workshop. No file downloads: packs inject config and styling directly.

## Installing mods
Drop exports into your mod folder (or a server's Mods directory): ELS CSVs, FGear INIs, mission JSON/Lua, skins + manifest. The game loads them at startup — no restarts needed for skins on most servers.`,
  },
  {
    id: "faq", label: "FAQ", icon: "help",
    body: `## Is New Citizen out?
Early development — Alpha on Steam. The game is entirely self-funded by founder Tanner Rozankovic (read the Founder Story); the roadmap page tracks milestones live.

## Is it free?
Premium title on Steam, no subscription. Community servers are free to join; hosting your own is free self-hosted or paid via 4NetPlayers.

## Is this FiveM?
No. New Citizen is a standalone Unreal Engine 5 game — not a GTA mod. If you're coming from FiveM RP, the rhythms will feel familiar but everything underneath is ours.

## What languages are supported?
The in-game menu, police tablet and phone ship in English, Español, Deutsch, Français, Português (BR) and Русский — pick yours in Settings → Language.

## Can I make content for it?
Yes — the Mod Hub suite (ELS lightbars, vehicle builds, liveries, missions) is built for exactly that, and content creators get an in-game Creator Studio with recording and broadcast overlays.

## Where's the community?
The official New Citizen Discord — discord.gg/NPhbFVSmpd (link in the New Citizen menu above).`,
  },
  {
    id: "creators", label: "Creators & Monetization", icon: "dollar",
    body: `## You own what you make
New Citizen is an open platform in the spirit of Garry's Mod. Server owners and modders keep FULL ownership of everything they create — mods, servers, liveries, communities, lore. SpecPixel claims no ownership of your work, ever.

## Monetize freely — we take 0%
- **Zero cut, forever** — charge for mods, server memberships, priority queue, cosmetics, donations, subscriptions. SpecPixel takes nothing and never processes your payments; the deal is between you and your supporters
- **Open market** — no price caps, no approval boards, no "approved storefronts". Overcharge for a bad product and the market sorts it out — players just won't buy
- **Your buyers, your responsibility** — deliver what you sell, handle your own refunds and taxes, and make sure you have the rights to every asset you ship

## The one hard line
Monetization never overrides the rules. The following is ALWAYS removed, no matter how much it earns: illegal content, sexually explicit material, hateful or racist content, harassment or doxxing, malware and cheats, stolen or ripped IP, real-money gambling, and anything violating Steam's rules. Uploads may be AI-scanned, the community can report, and three rejected uploads in 90 days triggers an automatic community suspension.

## Steam compliance
New Citizen is a premium Steam title and SpecPixel sets this UGC policy as publisher (the same model as Garry's Mod). Respect Valve's IP and the Steam Subscriber Agreement — never use UGC to circumvent Steam's systems.

## The full policy
Read the complete Creator & Monetization Policy on the website at specpixel.com/creator-terms.`,
  },
];

/* markdown-lite renderer shared by the public wiki page and the in-menu wiki */
export function renderWikiBody(body) {
  return body.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) return <p key={i} className="font-sans font-bold uppercase text-sm text-nc-blue mt-6 mb-2 first:mt-0">{block.slice(3)}</p>;
    if (block.startsWith("- ")) {
      return (
        <div key={i} className="flex flex-col gap-1.5 mb-3">
          {block.split("\n").map((li, j) => (
            <p key={j} className="font-mono text-[11px] text-zinc-400 leading-relaxed flex gap-2"><span className="text-nc-orange shrink-0">—</span>{li.slice(2).replace(/\*\*(.+?)\*\*/g, "$1")}</p>
          ))}
        </div>
      );
    }
    if (/^\d\./.test(block)) {
      return (
        <div key={i} className="flex flex-col gap-1.5 mb-3">
          {block.split("\n").map((li, j) => (
            <p key={j} className="font-mono text-[11px] text-zinc-400 leading-relaxed flex gap-2"><span className="text-nc-blue shrink-0 font-bold">{li.split(".")[0]}.</span>{li.split(". ").slice(1).join(". ").replace(/\*\*(.+?)\*\*/g, "$1")}</p>
          ))}
        </div>
      );
    }
    return <p key={i} className="font-mono text-[11px] text-zinc-400 leading-relaxed mb-3">{block.replace(/\*\*(.+?)\*\*/g, "$1")}</p>;
  });
}

/* full-text search over the knowledge base — powers the search boxes on the
   public wiki page and the in-menu wiki (mid-robbery lookups). Returns up to
   `limit` hits with a plain-text snippet from the first matching line. */
export function searchWiki(query, limit = 6) {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const hits = [];
  for (const s of WIKI_SECTIONS) {
    const plain = s.body.replace(/\*\*(.+?)\*\*/g, "$1");
    if (!`${s.label}\n${plain}`.toLowerCase().includes(q)) continue;
    const line = plain.split("\n").find((l) => !l.startsWith("## ") && l.toLowerCase().includes(q))
      || plain.split("\n").find((l) => l.toLowerCase().includes(q)) || "";
    hits.push({
      id: s.id, label: s.label, icon: s.icon,
      inTitle: s.label.toLowerCase().includes(q),
      snippet: line.replace(/^##\s*/, "").replace(/^[-\d. ]+/, "").trim().slice(0, 140),
    });
  }
  return hits.sort((a, b) => Number(b.inTitle) - Number(a.inTitle)).slice(0, limit);
}
